import React, { useState } from 'react';
import api from '../../utils/axiosInstance';
import AdminSideMenu from '../../components/AdminSideMenu';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';

const AdminAddCarrier = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [selectedLogo, setSelectedLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);

    const [formData, setFormData] = useState({
        companyName: '',
        trailer: [],
        contactName: '',
        jobTitle: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        website: '',
        companyDesc: '',
        password: ''
    });

    const trailerOptions = [
        { value: 'Van', label: 'Van' },
        { value: 'Flatbed', label: 'Flatbed' },
        { value: 'Reefer', label: 'Reefer' },
        { value: 'Tanker', label: 'Tanker' },
        { value: 'Other', label: 'Other' },
    ];

    const stateOptions = [
        "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
        "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky", "Louisiana",
        "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi", "Missouri", "Montana",
        "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico", "New York", "North Carolina",
        "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina",
        "South Dakota", "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
        "Wisconsin", "Wyoming"
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedLogo(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!formData.companyName || !formData.email || !formData.password) {
            alert('Please fill in required fields: Company Name, Email, and Password');
            return;
        }

        try {
            setLoading(true);
            console.log('📤 Starting submission...');

            const submitData = new FormData();

            // Append simple fields
            submitData.append('companyName', formData.companyName);
            submitData.append('contactName', formData.contactName || '');
            submitData.append('jobTitle', formData.jobTitle || '');
            submitData.append('email', formData.email);
            submitData.append('phone', formData.phone || '');
            submitData.append('address', formData.address || '');
            submitData.append('city', formData.city || '');
            submitData.append('state', formData.state || '');
            submitData.append('zip', formData.zip || '');
            submitData.append('website', formData.website || '');
            submitData.append('companyDesc', formData.companyDesc || '');
            submitData.append('password', formData.password);

            // Append trailer array - IMPORTANT: append each separately
            if (formData.trailer && formData.trailer.length > 0) {
                formData.trailer.forEach(t => {
                    submitData.append('trailer[]', t); // Note the [] suffix
                });
            }

            // Append logo if selected
            if (selectedLogo) {
                submitData.append('companyLogo', selectedLogo);
            }

            // Debug log
            console.log('📦 FormData contents:');
            for (let pair of submitData.entries()) {
                console.log(pair[0], pair[1]);
            }

            const res = await api.post('/carriers/direct', submitData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            console.log('✅ Success response:', res.data);
            alert('Carrier created successfully! ID: ' + res.data.carrier.identificationNumber);
            navigate('/admin/carriers-listing');

        } catch (error) {
            console.error('❌ Full error object:', error);
            console.error('❌ Error response:', error.response);
            console.error('❌ Error message:', error.message);

            let errorMessage = 'Failed to create carrier';

            if (error.response) {
                // Server responded with error
                errorMessage = error.response.data?.message
                    || error.response.data?.error
                    || `Server error: ${error.response.status}`;
                console.error('Server response data:', error.response.data);
            } else if (error.request) {
                // Request made but no response
                errorMessage = 'No response from server. Please check if the server is running.';
                console.error('Request was made but no response:', error.request);
            } else {
                // Error in request setup
                errorMessage = error.message;
            }

            alert(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handlePhoneChange = (e) => {
        let raw = e.target.value.replace(/\D/g, '').slice(0, 10); // Only digits, max 10
        let formatted = raw;
        if (raw.length > 6) {
            formatted = `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6, 10)}`;
        } else if (raw.length > 3) {
            formatted = `(${raw.slice(0, 3)}) ${raw.slice(3)}`;
        } else if (raw.length > 0) {
            formatted = `(${raw}`;
        }
        setFormData({ ...formData, phone: formatted });
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
            <AdminSideMenu />
            <div className="w-full md:ml-64 p-4 sm:p-6 lg:p-8">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-6">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Add New Carrier</h1>
                        <p className="text-gray-600">Create a carrier account directly</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <form onSubmit={handleSubmit} className="space-y-5">

                            {/* Company Name */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Company Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="companyName"
                                    value={formData.companyName}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>

                            {/* Trailer Types */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Endorsements Hauled<span className="text-red-500">*</span>
                                </label>
                                <Select
                                    isMulti
                                    options={trailerOptions}
                                    required
                                    value={trailerOptions.filter(opt => formData.trailer.includes(opt.value))}
                                    onChange={selected => {
                                        const values = selected ? selected.map(s => s.value) : [];
                                        setFormData(prev => ({ ...prev, trailer: values }));
                                    }}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    placeholder="Select trailer types"
                                />
                            </div>

                            {/* Contact Name and Job Title */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">
                                        Contact Person<span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="contactName"
                                        required
                                        value={formData.contactName}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">
                                        Job Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="jobTitle"
                                        value={formData.jobTitle}
                                        required
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                            </div>

                            {/* Email and Phone */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">
                                        Phone <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        name="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={handlePhoneChange}
                                        required
                                        maxLength={14}
                                        placeholder="(123) 456-7890"
                                        autoComplete="off"
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                            </div>

                            {/* Address */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    required
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>

                            {/* City, State, ZIP */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">
                                        City<span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="city"
                                        required
                                        value={formData.city}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">
                                        State<span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="state"
                                        value={formData.state}
                                        required
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    >
                                        <option value="">Select state</option>
                                        {stateOptions.map(st => (
                                            <option key={st} value={st}>{st}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-semibold text-gray-700">
                                        ZIP Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="zip"
                                        required
                                        value={formData.zip}
                                        onChange={handleInputChange}
                                        className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                    />
                                </div>
                            </div>

                            {/* Website */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Website
                                </label>
                                <input
                                    type="text"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleInputChange}
                                    placeholder="https://example.com"
                                    className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>

                            {/* Company Description */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Company Description
                                </label>
                                <textarea
                                    name="companyDesc"
                                    value={formData.companyDesc}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>
                            {/* Company Logo */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Company Logo
                                </label>
                                {logoPreview && (
                                    <div className="mb-3">
                                        <img
                                            src={logoPreview}
                                            alt="Logo Preview"
                                            className="w-24 h-24 rounded-full object-cover border-2 border-gray-300"
                                        />
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                    className="w-full border px-4 py-2 rounded file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700"
                                />
                            </div>
                            {/* Password */}
                            <div className="space-y-2">
                                <label className="block text-sm font-semibold text-gray-700">
                                    Password <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full border border-gray-300 px-4 py-2 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                />
                            </div>

                            {/* Submit Buttons */}
                            <div className="flex gap-4 pt-4">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                                >
                                    {loading ? 'Creating...' : 'Create Carrier'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => navigate('/admin/carriers-listing')}
                                    className="px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminAddCarrier;